import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'

import PaperAirplaneIcon from '@heroicons/react/outline/PaperAirplaneIcon'
import loadable from '@loadable/component'
import { Editor } from '@tiptap/react'
import clsx from 'clsx'
import { Prompt } from 'react-router'
import FontSizeIcon from 'remixicon-react/FontSizeIcon'
import Image2LineIcon from 'remixicon-react/Image2LineIcon'

import { Post } from '@tribeplatform/gql-client/types'
import { Button } from '@tribeplatform/react-ui-kit/Button'

import { memberToMentionConverter } from '../../lib/composer.utils'
import { getPostFieldValue } from '../../utils/post'
import { AttachmentsProps } from '../TipTap/@types'
import { Attachments } from '../TipTap/components/Attachment/Attachments'
import { AttachmentPickerButton } from '../TipTap/components/AttachmentPickerButton'
import { ComposerIconButton } from '../TipTap/components/ComposerIconButton'
import { EmojiPickerButton } from '../TipTap/components/EmojiPickerButton'
import { SlashMenuButton } from '../TipTap/components/SlashMenuButton'
import { TextFormattingMenu } from '../TipTap/components/TextBubbleMenu'
import { useTiptap } from '../TipTap/hooks/useTiptap'

const Tiptap = loadable(() => import('../TipTap'), {
  resolveComponent: module => module.Tiptap,
  ssr: false,
})

const TiptapProvider = loadable(() => import('../TipTap/TiptapProvider'), {
  resolveComponent: module => module.TiptapProvider,
  ssr: false,
})

export type ReplyEditorRef = {
  focus: () => void
}
export type ReplyEditorProps = {
  onReply: (content: string, editor: Editor) => void
  onCancel: (isTouched: boolean) => void
  reply?: Post
  isLoading?: boolean
  onAttachmentSelect?: (files: File[]) => void
} & Pick<AttachmentsProps, 'attachments' | 'onAttachmentDelete'>

export const ReplyEditor = forwardRef<ReplyEditorRef, ReplyEditorProps>(
  (props, ref) => {
    const { reply, onAttachmentSelect } = props
    const content =
      getPostFieldValue(reply, 'content') ||
      getPostFieldValue(reply, 'answer') ||
      ''

    return (
      <TiptapProvider
        content={content}
        embeds={reply?.embeds}
        mentions={reply?.mentions?.map(memberToMentionConverter)}
        handleAttachmentSelect={onAttachmentSelect}
      >
        <InnerReplyEditor {...props} ref={ref} />
      </TiptapProvider>
    )
  },
)
export const InnerReplyEditor = forwardRef<ReplyEditorRef, ReplyEditorProps>(
  (
    {
      onReply,
      onCancel,
      reply,
      attachments,
      onAttachmentDelete,
      isLoading,
      onAttachmentSelect,
    },
    ref,
  ) => {
    const [textFormattingOpen, setTextFormattingOpen] = useState(false)
    const { editor, isTouched, onSlashMenuItemClick, toggleDialog, state } =
      useTiptap()
    const isNew = !reply

    useEffect(() => {
      if (editor) {
        editor.setOptions({
          editorProps: {
            handleKeyDown: (view, event) => {
              if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                onReply(editor.getHTML(), editor)
                return true
              }
              return false
            },
          },
        })
      }
    }, [editor, onReply])

    useImperativeHandle(ref, () => ({
      focus: () => {
        editor.commands.focus()
      },
    }))

    return (
      <div
        style={{ minHeight: '160px' }} // min-h-40 not working?!
        className={clsx(
          'relative shadow-sm border rounded-md p-2 border-basicSurface-300/25 overflow-y-auto flex flex-col overflow-hidden',
          { 'my-2': !isNew },
        )}
      >
        <div className="flex-1">
          <Tiptap>
            {!!attachments?.length && (
              <div className="pb-5">
                <Attachments
                  attachments={attachments}
                  onAttachmentDelete={onAttachmentDelete}
                />
              </div>
            )}
          </Tiptap>
        </div>
        <Prompt
          when={isTouched}
          message={() => 'Changes you made may not be saved.'}
        />
        {editor && textFormattingOpen && (
          <TextFormattingMenu
            editor={editor}
            className="mb-2 shadow-none border-none px-0 overflow-x-auto"
          />
        )}
        <div className="justify-between flex items-end">
          {editor && (
            <div className="flex space-x-1 sm:space-x-2 items-center">
              <SlashMenuButton
                size="sm"
                onItemClick={props => {
                  setTimeout(() => {
                    onSlashMenuItemClick({
                      editor,
                      props,
                    })
                  }, 100)
                }}
              />

              <ComposerIconButton
                icon={FontSizeIcon}
                size="sm"
                aria-label="text formatting"
                onClick={() => {
                  setTextFormattingOpen(!textFormattingOpen)
                }}
                className={
                  textFormattingOpen
                    ? 'bg-surface-100 hover:bg-surface-200'
                    : ''
                }
              />

              <div className="hidden md:flex sm:space-x-2 items-center">
                <EmojiPickerButton size="sm" editor={editor} />
                <ComposerIconButton
                  size="sm"
                  icon={Image2LineIcon}
                  aria-label="image"
                  onClick={() => {
                    toggleDialog('image', true)
                  }}
                />
                <AttachmentPickerButton
                  size="sm"
                  onAttachmentSelect={onAttachmentSelect}
                />
              </div>
            </div>
          )}

          <div className="flex items-center ml-2">
            {typeof onCancel === 'function' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onCancel(isTouched)}
                className="mr-1 sm:mr-2"
              >
                Cancel
              </Button>
            )}
            <Button
              loading={isLoading || state.loading}
              size="sm"
              variant="primary"
              onClick={() => onReply(editor.getHTML(), editor)}
            >
              <span className="transform rotate-90">
                <PaperAirplaneIcon className="w-4 h-4" />
              </span>
            </Button>
          </div>
        </div>
      </div>
    )
  },
)
